// react
import axios from "axios";
import { createContext, useEffect, useReducer } from "react";

// utils

// Initial State
const initialState = {
  type: null,
  details: null,
  wb_agent: null,
  mmLiteStatus: null,
  canAddPhone: null,
  whatsAppSetupData: {},
  profileData: {},
  loader: true,
  phoneDetails: {},
};

// Action handlers
const handlers = {
  INITIALIZE: (state, action) => {
    const {
      type,
      details,
      mmLiteStatus,
      canAddPhone,
      whatsAppSetupData,
      phoneDetails,
    } = action.payload;
    return {
      ...state,
      type,
      details,
      mmLiteStatus,
      canAddPhone,
      whatsAppSetupData,
      phoneDetails,
      loader: false,
    };
  },

  GET_PROFILE_DATA: (state, action) => {
    const { profileData } = action.payload;
    return {
      ...state,
      profileData: profileData,
    };
  },
  SET_LOADER: (state, action) => {
    return {
      ...state,
      loader: action.payload,
    };
  },
};

// Create reducer
const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

// Create Context
export const WhatsAppSettingsContext = createContext();

// Utility functions for localStorage
const storageKeys = {
  API_HEADERS: "whatsapp_api_headers",
  USER_ID: "x-user-id",
  ORG_ID: "x-org-id",
};

const getStoredHeaders = () => {
  try {
    const stored = localStorage.getItem(storageKeys.API_HEADERS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error reading stored headers:", error);
  }
  return null;
};

const storeHeaders = (headers) => {
  try {
    localStorage.setItem(storageKeys.API_HEADERS, JSON.stringify(headers));
    // Also store individually for easy access
    if (headers["x-user-id"]) {
      localStorage.setItem(storageKeys.USER_ID, headers["x-user-id"]);
    }
    if (headers["x-org-id"]) {
      localStorage.setItem(storageKeys.ORG_ID, headers["x-org-id"]);
    }
  } catch (error) {
    console.error("Error storing headers:", error);
  }
};

const clearStoredHeaders = () => {
  try {
    localStorage.removeItem(storageKeys.API_HEADERS);
    localStorage.removeItem(storageKeys.USER_ID);
    localStorage.removeItem(storageKeys.ORG_ID);
  } catch (error) {
    console.error("Error clearing stored headers:", error);
  }
};

// Provider Component
export const GupshupSettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Function to get API headers (from localStorage or fetch new ones)
  const getApiHeaders = async () => {
    // First, try to get headers from localStorage
    const storedHeaders = getStoredHeaders();

    if (
      storedHeaders &&
      storedHeaders["x-user-id"] &&
      storedHeaders["x-org-id"]
    ) {
      console.log("Using stored API headers:", storedHeaders);
      return storedHeaders;
    }

    // If no stored headers, check if onboard_key is present in URL
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get("onboard_key");

    if (!key) {
      console.error("No onboard_key found in URL");
      return null;
    }

    // If onboard_key is present, fetch new headers
    try {
      const decodedKey = decodeURIComponent(key);
      const encodedKey = encodeURIComponent(decodedKey);
      const url = `https://adminapis.backendprod.com/partner_app/api/get_customer_config?onboard_key=${encodedKey}`;

      console.log("Fetching API headers from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Customer config response:", data);

      if (data.user_id && data.org_id) {
        const headers = {
          "Content-Type": "application/json",
          "x-user-id": data.user_id,
          "x-org-id": data.org_id,
        };

        // Store headers in localStorage for future use
        storeHeaders(headers);
        console.log("Headers stored in localStorage");

        return headers;
      } else {
        throw new Error("Missing user_id or org_id in response");
      }
    } catch (error) {
      console.error("Error fetching API headers:", error);
      return null;
    }
  };

  // Function to get current headers (for other components to use)
  const getCurrentHeaders = () => {
    return getStoredHeaders();
  };

  const initialize = async () => {
    try {
      dispatch({ type: "SET_LOADER", payload: true });

      // Step 1: Get API headers (from storage or fetch new using onboard_key)
      const headers = await getApiHeaders();

      if (!headers) {
        throw new Error("Failed to get API headers");
      }

      console.log("Making settings API call with headers:", headers);

      // Step 2: Make the main API call with headers
      const response = await axios.get(
        `https://onboardstaging.backendprod.com/api/org/whatsapp/settings`,
        { headers }
      );

      const res = await response.data;

      dispatch({
        type: "INITIALIZE",
        payload: {
          type:
            res?.settings && res?.settings?.type === "interakt"
              ? "interakt_cloud"
              : res?.settings?.type,
          details: res?.settings && res?.settings?.business,
          canAddPhone: res?.settings && res?.settings?.canAddPhone,
          mmLiteStatus: res?.settings && res?.settings?.mmLiteStatus,
          whatsAppSetupData: res?.settings && res?.settings,
          phoneDetails: res?.settings && res?.settings?.phone,
          loader: false,
        },
      });
    } catch (err) {
      console.error("Error in initialize:", err);
      dispatch({
        type: "INITIALIZE",
        payload: {
          type: null,
          details: null,
          mmLiteStatus: null,
          canAddPhone: null,
          whatsAppSetupData: {},
          phoneDetails: {},
          loader: false,
        },
      });
    }
  };

  const getProfileData = async () => {
    const headers = await getApiHeaders();
    try {
      const response = await axios.get(
        `https://onboardstaging.backendprod.com/api/org/whatsapp/profile`,
        { headers }
      );
      const res = await response.data;
      dispatch({
        type: "GET_PROFILE_DATA",
        payload: {
          profileData: res,
        },
      });
      return res;
    } catch (error) {
      throw new Error(error);
    }
  };

  // Initialize on component mount
  useEffect(() => {
    const initializeOnLoad = async () => {
      // Check if onboard_key is present in URL
      const urlParams = new URLSearchParams(window.location.search);
      const onboardKey = urlParams.get("onboard_key");

      if (onboardKey) {
        console.log("onboard_key found in URL, initializing...");
        await initialize();
        await getProfileData();
      } else {
        console.log(
          "No onboard_key found in URL, checking for stored headers..."
        );
        // If no onboard_key, check if we have stored headers and try to initialize
        const storedHeaders = getStoredHeaders();
        if (
          storedHeaders &&
          storedHeaders["x-user-id"] &&
          storedHeaders["x-org-id"]
        ) {
          console.log("Found stored headers, initializing...");
          await initialize();
        } else {
          console.log("No stored headers found, setting loader to false");
          dispatch({ type: "SET_LOADER", payload: false });
        }
      }
    };

    initializeOnLoad();
  }, []); // Empty dependency array to run only on mount

  return (
    <WhatsAppSettingsContext.Provider
      value={{
        type: state.type,
        details: state.details,
        mmLiteStatus: state.mmLiteStatus,
        canAddPhone: state.canAddPhone,
        wb_agent: state.wb_agent,
        whatsAppChatAgents: state.whatsAppChatAgents,
        whatsAppChatAgentsError: state.whatsAppChatAgentsError,
        whatsAppSetupData: state.whatsAppSetupData,
        loader: state.loader,
        phoneDetails: state.phoneDetails,
        profileData: state.profileData,
        getApiHeaders, // Export so other components can get headers
        getCurrentHeaders, // Export to get current headers
        clearStoredHeaders, // Export to clear headers if needed
        initialize, // Export initialize function for manual re-initialization
      }}
    >
      {children}
    </WhatsAppSettingsContext.Provider>
  );
};
